import { Box } from '@mui/material';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { ExpiringDocumentsReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/People/ExpiringDocumentsReport/useStyles';
import {
  convertToDateOrUndefined,
  getFormattedDateString,
} from '~/utils/helpers/date';

export type ExpiringDocumentsReportDataRow = {
  id: number;
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  unitId: number;
  unitName: string;
  unitNumber: string;
  managerName: string;
  documentId: number;
  documentType: string;
  documentName: string;
  documentExpDate?: Date;
  daysToExpiration: string | number;
};

type PropsType = {
  data: ExpiringDocumentsReportData[];
};

export const useExpiringDocumentsReport = (props: PropsType) => {
  const { data } = props;

  const { t } = useTranslation();

  const sxStyles = useSxStyles();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEnums } = useAllEnums();

  const { getEmployeeById } = useAllEmployees();

  const enums = useMemo(() => {
    return {
      countryEnums: getEnums(EnumListKey.COUNTRY),
      documentTypeEnums: getEnums(EnumListKey.DOCUMENT_TYPE),
    };
  }, [getEnums]);

  const rows: ExpiringDocumentsReportDataRow[] = useMemo(() => {
    const { documentTypeEnums } = enums;

    const getDocumentType = (documentTypeCode: string): string =>
      documentTypeEnums?.find((e) => e.code === documentTypeCode)?.name ?? '';

    const getManagerName = (managerId: number) => {
      const manager = getEmployeeById(managerId);
      return manager ? `${manager.fEmpFirstName} ${manager.fEmpLastName}` : '';
    };

    const getDaysToExpiration = (expirationDate: string): string | number => {
      const today = moment().startOf('day');
      if (moment(expirationDate).isBefore(today, 'date')) {
        return t('laExpired');
      }

      return moment(expirationDate).diff(today, 'days');
    };

    return data
      .map((employee) => {
        return employee.fEmpExpiringDocuments.map(
          (document): ExpiringDocumentsReportDataRow => {
            return {
              id: document.fEmpDocumentId,
              employeeId: employee.fEmpId,
              firstName: employee.fEmpFirstName,
              lastName: employee.fEmpLastName,
              employeeNumber: employee.fEmpEmployeeNumber,
              unitId: employee.fEmpUnitId,
              unitName: employee.fEmpUnit,
              unitNumber: employee.fEmpUnitNumber,
              managerName: getManagerName(employee.fEmpManagerId),
              documentId: document.fEmpDocumentId,
              documentType: getDocumentType(document.fEmpDocumentType),
              documentName: document.fEmpDocumentName,
              documentExpDate: convertToDateOrUndefined(
                document.fEmpDocumentExpDate,
              ),
              daysToExpiration: getDaysToExpiration(
                document.fEmpDocumentExpDate,
              ),
            };
          },
        );
      })
      .flat();
  }, [t, data, enums, getEmployeeById]);

  const { columnsConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<ExpiringDocumentsReportDataRow>[] =
      [
        {
          field: 'employeeId',
          headerName: t('laPicture'),
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (params) => {
            return (
              <EmployeeCellComponent
                {...params}
                pictureOnly={true}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 30,
        },
        {
          field: 'firstName',
          headerName: t('laFirstName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 90,
        },
        {
          field: 'lastName',
          headerName: t('laLastName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
              />
            );
          },
          minWidth: 110,
        },
        {
          field: 'unitName',
          headerName: t('laOrgUnit'),
          minWidth: 180,
        },
        {
          field: 'unitNumber',
          headerName: t('laUnitNumber'),
          minWidth: 180,
        },
        {
          field: 'managerName',
          headerName: t('laManager'),
          minWidth: 180,
        },
      ];

    const documentColumnConfigs: GridColDef<ExpiringDocumentsReportDataRow>[] =
      [
        {
          field: 'documentType',
          headerName: t('laDocumentType'),
          minWidth: 150,
        },
        {
          field: 'documentName',
          headerName: t('laDocumentName'),
          minWidth: 250,
        },
        {
          field: 'documentExpDate',
          headerName: t('laExpiringDate'),
          minWidth: 150,
          type: 'date',
          renderCell: ({ value }) => {
            const isExpired = moment(value).isBefore(moment().startOf('day'));
            return (
              <Box sx={isExpired ? sxStyles.isExpired : undefined}>
                {getFormattedDateString(value, 'L')}
              </Box>
            );
          },
        },
        {
          field: 'daysToExpiration',
          headerName: t('laDaysToExpir'),
          minWidth: 150,
          renderCell: ({ value }) => {
            return (
              <Box sx={isNaN(value) ? sxStyles.isExpired : undefined}>
                {value}
              </Box>
            );
          },
        },
      ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'document',
        headerName: t('document'),
        children: getFieldNamesFromColumnConfig(documentColumnConfigs),
      },
    ];

    return {
      columnsConfigs: [...employeeColumnConfigs, ...documentColumnConfigs],
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, sxStyles]);

  return {
    table: {
      rows,
      columnsConfigs,
      columnGroupingModel,
    },
  };
};
